// Protects a Realtime conversation from the avatar hearing itself through the
// speakers. Browser echo cancellation removes most of the leak; this module
// covers what still reaches the model: a transcribed "user turn" that is
// really the avatar's own words, and the reply the model then makes to itself.

export type EchoGuardEvent = {
  type?: string;
  transcript?: unknown;
  response?: { id?: string } | null;
};

export type EchoVerdict = { ignoreTranscript: boolean; cancelResponse: boolean };

const tokens = (text: unknown): string[] =>
  String(text ?? "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s']/gu, " ")
    .split(/\s+/)
    .filter(Boolean);

/** True when the transcribed user speech repeats something the avatar just said. */
export function isEchoOfOwnSpeech(userText: unknown, assistantTexts: readonly string[]): boolean {
  const user = tokens(userText);
  if (user.length < 2) return false;
  const phrase = ` ${user.join(" ")} `;
  for (const spoken of assistantTexts) {
    const said = tokens(spoken);
    if (!said.length) continue;
    if (` ${said.join(" ")} `.includes(phrase)) return true;
    // Transcription noise: nearly every word of a longer turn matches.
    const vocabulary = new Set(said);
    const hits = user.filter((word) => vocabulary.has(word)).length;
    if (user.length >= 3 && hits / user.length >= 0.85) return true;
  }
  return false;
}

export type EchoGuard = {
  /** Whether the avatar is audibly playing, from WebRTC output buffer events. */
  readonly assistantSpeaking: boolean;
  handle(event: EchoGuardEvent, now?: number): EchoVerdict;
};

/**
 * Tracks the avatar's recent speech and playback state from Realtime events.
 * handle() returns what the caller should do with the event: ignore an echoed
 * transcript, and cancel the response the echo triggered.
 */
export function createEchoGuard({ windowMs = 1500, remember = 3 } = {}): EchoGuard {
  const spoken: string[] = [];
  let activeResponse: string | null = null;
  let suppressUntil = -Infinity;
  const none = (): EchoVerdict => ({ ignoreTranscript: false, cancelResponse: false });
  const guard = {
    assistantSpeaking: false,
    handle(event: EchoGuardEvent, now: number = Date.now()): EchoVerdict {
      switch (event?.type) {
        case "response.created":
          activeResponse = event.response?.id || "active";
          if (now < suppressUntil) {
            suppressUntil = -Infinity;
            return { ...none(), cancelResponse: true };
          }
          return none();
        case "response.done":
          if (!event.response?.id || event.response.id === activeResponse) activeResponse = null;
          return none();
        case "response.output_audio_transcript.done":
        case "response.audio_transcript.done": {
          const text = String(event.transcript ?? "").trim();
          if (text) {
            spoken.push(text);
            if (spoken.length > remember) spoken.shift();
          }
          return none();
        }
        case "output_audio_buffer.started":
          guard.assistantSpeaking = true;
          return none();
        case "output_audio_buffer.stopped":
        case "output_audio_buffer.cleared":
          guard.assistantSpeaking = false;
          return none();
        case "conversation.item.input_audio_transcription.completed": {
          if (!isEchoOfOwnSpeech(event.transcript, spoken)) return none();
          if (activeResponse) return { ignoreTranscript: true, cancelResponse: true };
          // The self-reply has not started yet; cancel it if it starts soon.
          suppressUntil = now + windowMs;
          return { ignoreTranscript: true, cancelResponse: false };
        }
        default:
          return none();
      }
    },
  };
  return guard;
}

/**
 * With confirmed echo cancellation the conversation stays full duplex so the
 * user can talk over the avatar. Without it, the microphone is closed while
 * the avatar is audibly speaking so its own voice cannot interrupt it.
 */
export function microphoneEnabled({
  hostMuted,
  assistantSpeaking,
  echoCancellationConfirmed,
}: {
  hostMuted: boolean;
  assistantSpeaking: boolean;
  echoCancellationConfirmed: boolean;
}): boolean {
  if (hostMuted) return false;
  return echoCancellationConfirmed || !assistantSpeaking;
}
